import maplibregl, {
  ColorSpecification,
  DataDrivenPropertyValueSpecification,
  LineLayerSpecification,
  Map as MlMap,
} from 'maplibre-gl';
import * as turf from '@turf/turf';
import { LineString } from 'geojson';
import { getFirstLabelId } from '@/utils/map';
import { SOURCE_ID } from '@/components/ui/map/sources/NewCoverageData';
import { SelectedFeature, ISelectionPayload } from '@/types/scout';
import { combineLinesToMultiLineString } from '@/utils/scout';
import {
  MAP_CENTROID_LAYER,
  MAP_GEOJSON_LAYER,
  MAP_GEOJSON_STROKE,
  MAP_SEGMENTS_LAYER,
  MAP_SEGMENTS_STROKE,
} from '@/components/ui/scout/map/Map';
import * as CoverageData from '@/components/ui/map/sources/NewCoverageData';
import * as RoadData from '@/components/ui/map/sources/RoadData';

interface IClassLayer {
  name: SegmentClass;
  paintProps: LineLayerSpecification['paint'];
  outlinePaintProps?: LineLayerSpecification['paint'];
  selectedPaintProps?: LineLayerSpecification['paint'];
}

enum SegmentClass {
  Unclassified = 'class-unclassified',
  Other = 'class-other',
  ParkingAisle = 'class-parkingAisle',
  Driveway = 'class-driveway',
  Trunk = 'class-trunk',
  LivingStreet = 'class-livingStreet',
  Tertiary = 'class-tertiary',
  Secondary = 'class-secondary',
  Residential = 'class-residential',
  Primary = 'class-primary',
  Motorway = 'class-motorway',
  MotorwayZoomed = 'class-motorwayZoomed',
}

export const selectedFeaturesMap = new Map<string | number, SelectedFeature>();
export const getSelectedFeaturesMap = () => selectedFeaturesMap;
export const clearSelectedFeaturesMap = () => selectedFeaturesMap.clear();

function getLayerId(layerName: string) {
  return `hm-${layerName}`;
}

export const CLASS_LAYERS: IClassLayer[] = [
  {
    name: SegmentClass.Unclassified,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.Unclassified),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(
        SegmentClass.Unclassified,
        4,
      ),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.Unclassified, 4),
    },
  },
  {
    name: SegmentClass.Other,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.Other),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(SegmentClass.Other, 4),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.Other, 4),
    },
  },
  {
    name: SegmentClass.ParkingAisle,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.ParkingAisle),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(
        SegmentClass.ParkingAisle,
        4,
      ),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.ParkingAisle, 4),
    },
  },
  {
    name: SegmentClass.Driveway,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.Driveway),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(SegmentClass.Driveway, 4),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.Driveway, 4),
    },
  },
  {
    name: SegmentClass.Trunk,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.Trunk),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(SegmentClass.Trunk, 4),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.Trunk, 4),
    },
  },
  {
    name: SegmentClass.LivingStreet,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.LivingStreet),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(
        SegmentClass.LivingStreet,
        4,
      ),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.LivingStreet, 4),
    },
  },
  {
    name: SegmentClass.Tertiary,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.Tertiary),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(SegmentClass.Tertiary, 4),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.Tertiary, 4),
    },
  },
  {
    name: SegmentClass.Secondary,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.Secondary),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(SegmentClass.Secondary, 4),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.Secondary, 4),
    },
  },
  {
    name: SegmentClass.Residential,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.Residential),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(SegmentClass.Residential, 4),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.Residential, 4),
    },
  },
  {
    name: SegmentClass.Primary,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.Primary),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(SegmentClass.Primary, 4),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.Primary, 4),
    },
  },
  {
    name: SegmentClass.Motorway,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.Motorway),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(SegmentClass.Motorway, 4),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.Motorway, 4),
    },
  },
  {
    name: SegmentClass.MotorwayZoomed,
    paintProps: {
      'line-color': getLineColor(),
      'line-width': getLineWidthBySegmentClass(SegmentClass.Motorway),
    },
    outlinePaintProps: {
      'line-gap-width': getLineWidthBySegmentClass(SegmentClass.Motorway, 4),
    },
    selectedPaintProps: {
      'line-width': getLineWidthBySegmentClass(SegmentClass.Motorway, 4),
    },
  },
];
const layerIds = CLASS_LAYERS.map(layer => getLayerId(layer.name));
let selectedSegment: maplibregl.MapGeoJSONFeature | null;

export function addLayer(
  map: MlMap,
  /** Enables the following:
   *  - The road segment becomes selectable
   *  - Options on which map events to bind & optional callback **/
  selectionOptions?: {
    type: 'coverage' | 'scout';
    beforeId?: {
      selection: string;
      outline: string;
    };
    zoomThreshold?: number;
    callback?: (param: any) => void;
  },
  source: string = SOURCE_ID,
) {
  CLASS_LAYERS.forEach(layer => {
    const layerId = getLayerId(layer.name);
    if (!map.getLayer(layerId)) {
      map.addLayer(
        {
          id: source + layerId,
          type: 'line',
          source,
          'source-layer': layer.name,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            'line-round-limit': 0,
          },
          paint: {
            'line-blur': 1,
            ...layer.paintProps,
          },
        },
        selectionOptions?.beforeId?.outline ?? getFirstLabelId(map),
      );
    }

    // Add the outline layer for selected segments.
    map.addLayer(
      {
        id: `hm-selected-${source + layer.name}-outline`,
        type: 'line',
        source,
        'source-layer': layer.name,
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#000000',
          'line-width': 5,
          'line-opacity': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            1,
            0,
          ],
          ...layer.outlinePaintProps,
        },
        minzoom: selectionOptions?.zoomThreshold ?? 0,
      },
      selectionOptions?.beforeId?.outline ?? getFirstLabelId(map),
    );
  });

  if (selectionOptions) {
    bindCoverageEvents(map, source, selectionOptions);
  }
}

const addSelectableLayer = (
  map: MlMap,
  source: string = SOURCE_ID,
  layer: IClassLayer,
  selectionOptions?: {
    type: 'coverage' | 'scout';
    beforeId?: {
      selection: string;
      outline: string;
    };
    zoomThreshold?: number;
    callback?: (param: any) => void;
  },
) => {
  map.addLayer(
    {
      id: `hm-selected-${source + layer.name}`,
      type: 'line',
      source,
      'source-layer': layer.name,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': '#fff',
        'line-opacity': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          1,
          0,
        ],
        ...layer.selectedPaintProps,
      },
      minzoom: selectionOptions?.zoomThreshold ?? 0,
    },
    selectionOptions?.beforeId?.selection,
  );
};

const bindCoverageEvents = (
  map: MlMap,
  source: string = SOURCE_ID,
  selectionOptions?: {
    type: 'coverage' | 'scout';
    beforeId?: {
      selection: string;
      outline: string;
    };
    zoomThreshold?: number;
    callback?: (param: any) => void;
  },
) => {
  const zoomThreshold = selectionOptions?.zoomThreshold || 10;

  // Add popup w/ segment info to the map.
  const popup = new maplibregl.Popup({
    // We close the popup manually to have more granular control.
    closeOnMove: false,
    closeOnClick: false,
    offset: [0, -20],
    className:
      'rounded-lg bg-neutral-1000 px-[18px] py-4 font-[lato] text-neutral-0',
  });

  // Add the highlighted/selected version of the segment line as a layer.
  CLASS_LAYERS.forEach(layer => {
    addSelectableLayer(map, source, layer, selectionOptions);
  });

  map.on('click', e => {
    onClickHandler(e, source, popup, selectionOptions);
  });

  // Add the event handlers for hover.
  layerIds.forEach(layerId => {
    // Change the cursor to a pointer on hover segment.
    map.on('mouseenter', source + layerId, e => {
      const currentZoom = e.target.getZoom();
      if (currentZoom < zoomThreshold) return;
      if (!e.features || e.features.length === 0) return;

      map.getCanvas().style.cursor = 'pointer';
    });
    // Change it back to a pointer when it leaves.
    map.on('mouseleave', source + layerId, () => {
      if (map.getCanvas().style.cursor === 'pointer') {
        map.getCanvas().style.cursor = '';
      }
    });
  });
};

/**
 * Handles the segment selection on the Scout Map. For each segment selected we validate:
 *   - If anoter feature with the same id exists, if so we merge them together if their geometry is different
 *   - If the geometry created by the selected segments create a contiguous line. If so, a valid location with this geometry is created
 */
const handleScoutSelection = (
  e: maplibregl.MapMouseEvent,
  source: string = SOURCE_ID,
  selectionCallback?: (payload: ISelectionPayload | null) => void,
  zoomThreshold: number = 10,
) => {
  const { target: map } = e;

  const currentZoom = map.getZoom();
  if (currentZoom < zoomThreshold) return;

  const sourceAndLayerIds = layerIds.map(layerId => source + layerId);

  const features = map.queryRenderedFeatures(e.point, {
    layers: [
      ...sourceAndLayerIds,
      MAP_CENTROID_LAYER,
      MAP_GEOJSON_LAYER,
      MAP_GEOJSON_STROKE,
      MAP_SEGMENTS_LAYER,
      MAP_SEGMENTS_STROKE,
    ],
  });

  const targetFeature = features[0];
  if (!targetFeature) return;

  const layerNames = features.map(feature => feature.layer.id);
  if (
    [
      MAP_CENTROID_LAYER,
      MAP_GEOJSON_LAYER,
      MAP_GEOJSON_STROKE,
      MAP_SEGMENTS_LAYER,
      MAP_SEGMENTS_STROKE,
    ].some(layer => layerNames.includes(layer))
  ) {
    return;
  }

  const { id, source: tfSource, sourceLayer, properties } = targetFeature;
  if (!id || !source || !sourceLayer) return;

  // if current source is RoadData, verify that the map feature is not superimposed to the coverage layer
  if (source === RoadData.SOURCE_ID) {
    const features = map.queryRenderedFeatures(e.point);
    const layerNames = features.map(feature => feature.layer.id);

    if (layerNames.some(layer => layer.includes(CoverageData.SOURCE_ID))) {
      return;
    }
  }

  const featureState = map.getFeatureState({
    id,
    source: tfSource,
    sourceLayer,
  });

  if (featureState?.selected) {
    map.setFeatureState(
      { source: tfSource, sourceLayer, id },
      { selected: false },
    );
    selectedFeaturesMap.delete(id!);
  } else {
    map.setFeatureState(
      { source: tfSource, sourceLayer, id },
      { selected: true },
    );

    const featuresWithId = map.queryRenderedFeatures({
      layers: [...sourceAndLayerIds],
      filter: ['==', 'id', id],
    });

    const uniqueGeometry = featuresWithId
      .filter(
        (feature, index, self) =>
          index === self.findIndex(t => turf.booleanEqual(t, feature)),
      )
      .map(f => f.geometry) as LineString[];

    const multiLineString = combineLinesToMultiLineString(uniqueGeometry);

    selectedFeaturesMap.set(id, {
      id,
      segments: multiLineString,
      source: tfSource,
      sourceLayer,
      name: properties?.name,
      coverage: Math.ceil(properties?.coverage ?? 0),
    });
  }

  const uniqueFeatures = Array.from(selectedFeaturesMap.values());
  const lines = uniqueFeatures.map(f => f.segments);

  const polygons = uniqueFeatures
    .map(f => {
      // create a small buffer around the segment to validate if all segments are contiguous, there can be a slight gap between segments
      const polygon = turf.buffer(f.segments, 0.5, {
        units: 'meters',
      });

      return polygon;
    })
    .filter(p => !!p);

  let mergedLines;
  let mergedPolygons;

  if (polygons.length > 1) {
    try {
      mergedPolygons = turf.union(turf.featureCollection(polygons));
      mergedLines = combineLinesToMultiLineString(lines);
    } catch (error) {
      mergedPolygons = polygons[0];
      mergedLines = lines[0];
    }
  } else {
    mergedPolygons = polygons[0];
    mergedLines = lines[0];
  }

  if (mergedPolygons) {
    // if contiguous the merged polygons will be a single polygon (not a multi-polygon)
    const isContiguous = turf.getType(mergedPolygons) === 'Polygon';

    // create a 20 meter buffer around the merged lines to represent the location
    const polygon = turf.buffer(mergedLines, 20, {
      units: 'meters',
    });

    if (!polygon) {
      selectionCallback && selectionCallback(null);
      return;
    }

    selectionCallback &&
      selectionCallback({
        polygon: polygon?.geometry,
        segments: mergedLines,
        isContiguous,
      });
  } else {
    selectionCallback && selectionCallback(null);
  }
};

const handleCoverageSelection = (
  e: maplibregl.MapMouseEvent,
  source: string = SOURCE_ID,
  popup: maplibregl.Popup,
) => {
  const { lngLat, target: map } = e;

  const sourceAndLayerIds = layerIds.map(layerId => source + layerId);

  const features = map.queryRenderedFeatures(e.point, {
    layers: sourceAndLayerIds,
  });

  // Leave any popup open if click and dragging.
  if (map.isMoving()) {
    return;
  }

  // Close the popup if it's open.
  if (popup.isOpen()) {
    popup.remove();
  }

  // If we have a selected segment, deselect it.
  if (selectedSegment) {
    const { source, sourceLayer, id } = selectedSegment;
    map.setFeatureState({ source, sourceLayer, id }, { selected: false });
    selectedSegment = null;

    // If clicking on the same segment, don't select it again.
    if (id && id === features[0]?.id) {
      return;
    }
  }

  // Only enable popup when zoom is at least 12.
  const currentZoom = map.getZoom();
  if (currentZoom < 10) return;
  // No-op if the click is not on a segment.
  const targetFeature = features[0];
  if (!targetFeature) return;
  const { id, source: tfSource, sourceLayer } = targetFeature;
  if (!id || !source || !sourceLayer) return;

  map.setFeatureState(
    { source: tfSource, sourceLayer, id },
    { selected: true },
  );
  selectedSegment = targetFeature;

  const { coverage, name } = targetFeature.properties;
  popup
    .setLngLat(lngLat)
    .setHTML(getPopupContentHtml({ coverage, name }))
    .addTo(map);
};

function onClickHandler(
  e: maplibregl.MapMouseEvent,
  source: string = SOURCE_ID,
  popup: maplibregl.Popup,
  selectionOptions?: {
    type: 'coverage' | 'scout';
    beforeId?: {
      selection: string;
      outline: string;
    };
    zoomThreshold?: number;
    callback?: (param: any) => void;
  },
) {
  if (selectionOptions?.type === 'coverage') {
    handleCoverageSelection(e, source, popup);
  } else if (selectionOptions?.type === 'scout') {
    handleScoutSelection(
      e,
      source,
      selectionOptions.callback,
      selectionOptions.zoomThreshold,
    );
  }
}

function getPopupContentHtml({
  coverage,
  name,
}: {
  coverage: number;
  name: string;
}): string {
  const numTimesMapped = Math.ceil(coverage ?? 0);

  return `
      <div class="text-lg font-bold leading-4">
        ${numTimesMapped} ${numTimesMapped > 1 ? 'times' : 'time'}
      </div>
      <div 
        class="mt-[8px] text-sm leading-4 ${!name ? 'hidden' : ''}"
      >
        ${name}
      </div>
    `;
}

/** Call {@link getLineWidth} with the appropriate parameters given a segment class. */
function getLineWidthBySegmentClass(
  segmentClass: SegmentClass,
  extraWidth = 0,
): DataDrivenPropertyValueSpecification<number> {
  switch (segmentClass) {
    case SegmentClass.Unclassified:
      return getLineWidth({
        minZoom: 10,
        minWidth: 0.1,
        maxWidth: 5 + extraWidth,
      });
    case SegmentClass.Other:
      return getLineWidth({
        minZoom: 13,
        minWidth: 0.1,
        maxWidth: 5 + extraWidth,
      });
    case SegmentClass.ParkingAisle:
      return getLineWidth({
        minZoom: 13,
        minWidth: 0.1,
        maxWidth: 4 + extraWidth,
      });
    case SegmentClass.Driveway:
      return getLineWidth({
        minZoom: 13,
        minWidth: 0.1,
        maxWidth: 3 + extraWidth,
      });
    case SegmentClass.Trunk:
      return getLineWidth({
        minZoom: 1,
        minWidth: 0.1,
        maxWidth: 8 + extraWidth,
      });
    case SegmentClass.LivingStreet:
      return getLineWidth({
        minZoom: 10,
        minWidth: 0.1,
        maxWidth: 6 + extraWidth,
      });
    case SegmentClass.Tertiary:
      return getLineWidth({
        minZoom: 8,
        minWidth: 0.1,
        maxWidth: 6 + extraWidth,
      });
    case SegmentClass.Secondary:
      return getLineWidth({
        minZoom: 7,
        minWidth: 0.1,
        maxWidth: 7 + extraWidth,
      });
    case SegmentClass.Residential:
      return getLineWidth({
        minZoom: 10,
        minWidth: 0.1,
        maxWidth: 6 + extraWidth,
      });
    case SegmentClass.Primary:
      return getLineWidth({
        minZoom: 1,
        minWidth: 0.1,
        maxWidth: 8 + extraWidth,
      });
    case SegmentClass.Motorway:
    case SegmentClass.MotorwayZoomed:
      return getLineWidth({
        minZoom: 1,
        minWidth: 0.1,
        maxWidth: 10 + extraWidth,
        exponential: 1.25,
      });
    default:
      throw new Error(`Invalid segment class: ${segmentClass}`);
  }
}

/** Generates the line-width expression given specific values. */
function getLineWidth({
  minZoom,
  maxZoom = 17,
  minWidth,
  maxWidth,
  exponential = 1.2,
}: {
  minZoom: number;
  maxZoom?: number;
  minWidth: number;
  maxWidth: number;
  exponential?: number;
}): DataDrivenPropertyValueSpecification<number> {
  return [
    'interpolate',
    ['exponential', exponential],
    ['zoom'],
    minZoom,
    minWidth,
    maxZoom,
    maxWidth,
  ];
}

function getLineColor(): DataDrivenPropertyValueSpecification<ColorSpecification> {
  return [
    'case',
    // If the 'coverage' property does not exist, set the line color to transparent
    ['!', ['has', 'coverage']],
    'rgba(0, 0, 0, 0)',
    // If 'coverage' exists, interpolate based on the 'coverage' value
    [
      'interpolate',
      ['exponential', 0.98],
      ['get', 'coverage'],
      1,
      '#065c74', // Darker blue at lower coverage
      200,
      '#cef2fd', // Lighter blue at higher coverage
    ],
  ];
}
